import { Link, useParams } from 'react-router-dom'
import {
  ApiError,
  api,
  type Bracket as Drawn,
  type BracketProjection,
  type BracketSeed,
  type BracketSource,
  type EventSummary,
} from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { useSeason } from './useSeason'
import { SeasonSwitcher } from './SeasonSwitcher'
import { usePlayerPaths } from '../../components/playerPaths'

/**
 * The playoff bracket, and — before the cut is taken — the projection of one
 * (FR-035, FR-063).
 *
 * # Two routes, two renderings, never one with a flag
 *
 * `/bracket` is a 404 until the commissioner takes the cut, which is the
 * ordinary state of a season in progress. So this view asks for the drawn
 * bracket first and falls back to `/bracket/projection`, and **renders the two
 * differently on purpose**: a projected seeding must never be presentable as a
 * settled one. The projection carries the same four defences the standings
 * projection does — its own route, a banner that cannot be scrolled past, a
 * visible tag in the heading, and a statement of how provisional it is.
 *
 * # Who is OUT is as much the answer as who is in
 *
 * Every player is listed, above and below the cut, because the interesting
 * question is usually why someone missed it. In 2025-26 Shawn and Dad both
 * finished on seven points and the match record separated them — head-to-head
 * would have gone the other way — so the tier that decided it is named on the
 * row rather than left to be inferred from two equal numbers.
 *
 * # An empty slot says what it is waiting for
 *
 * The final's participants are the knockout's winners. Rendering that as a
 * blank would read as missing data; it is rendered as the source itself —
 * "the winner of round 1 slot 1" — which is what it actually is.
 */
export function Bracket({ only }: { only?: 'projection' } = {}) {
  const { seasonId } = useParams()
  const season = useSeason(seasonId)
  const id = season.state === 'ready' ? season.data.id : 0

  // **`only="projection"` is its own URL, mirroring the API's two routes.**
  //
  // Once a cut has been taken, `/bracket` serves the drawn one and the
  // projection has nowhere to render — so the question "how would this be
  // seeded from today's table?" became unaskable of any season that had
  // answered it. The server never had that problem: it has always been two
  // routes, for the reason FR-063 gives, and the client now has the same two.
  const wantOnlyProjection = only === 'projection'
  const drawn = useApi(
    (s) => (id && !wantOnlyProjection ? api.bracket(id, s) : never()),
    [id, wantOnlyProjection],
  )
  // Only asked for once the bracket is known to be absent, so a season that has
  // one costs a single request.
  const undrawn = wantOnlyProjection || (drawn.state === 'error' && isNotFound(drawn.error))
  const projected = useApi((s) => (id && undrawn ? api.bracketProjection(id, s) : never()), [
    id,
    undrawn,
  ])

  if (season.state === 'loading') return <Loading what="the season" />
  if (season.state === 'error') return <Failed what="The season" error={season.error} />
  if (!wantOnlyProjection) {
    if (drawn.state === 'loading') return <Loading what="the bracket" />
    if (drawn.state === 'ready') {
      return <DrawnBracket season={season.data.name} seasonId={id} bracket={drawn.data} />
    }
    if (!undrawn) return <Failed what="The bracket" error={drawn.error} />
  }
  if (projected.state === 'loading') return <Loading what="the projected bracket" />
  if (projected.state === 'error') {
    // A season with no playoff format is the honest 404 here, and it is a
    // different sentence from a failure.
    return isNotFound(projected.error) ? (
      <section>
        <h2>{season.data.name} playoffs</h2>
        <SeasonSwitcher current={id} view="bracket" />
        <Empty>This season has no playoff.</Empty>
      </section>
    ) : (
      <Failed what="The projected bracket" error={projected.error} />
    )
  }
  return <ProjectedBracket season={season.data.name} seasonId={id} projection={projected.data} />
}

function DrawnBracket({
  season,
  seasonId,
  bracket,
}: {
  season: string
  seasonId: number
  bracket: Drawn
}) {
  const champion = bracket.seeds.find((s) => s.player_id === bracket.champion_player_id)
  return (
    <section className="bracket" data-testid="bracket">
      <h2>{season} playoffs</h2>
      <SeasonSwitcher current={seasonId} view="bracket" />

      <p className="note" data-testid="bracket-seeded-at">
        Seeded on {formatDate(bracket.seeded_at)}, {bracket.qualifiers} of {bracket.seeds.length}{' '}
        qualifying. <strong>This seeding is frozen.</strong> It is the record of what governed, and
        it is what the playoff handicap adjustment was computed from — so it does not move as the
        table does. Ordered on {bracket.seeding_rule.order.map(tierName).join(', then ')}.
      </p>

      {champion && (
        <p className="bracket-champion" data-testid="bracket-champion">
          <strong>{champion.display_name}</strong> won it.
        </p>
      )}

      <Seeds seeds={bracket.seeds} qualifiers={bracket.qualifiers} />

      <h3>The draw</h3>
      <ol className="bracket-rounds" data-testid="bracket-rounds">
        {bracket.rounds.map((round) => (
          <li key={round.round} className="bracket-round">
            <h4>
              <Link to={`/events/${round.event_id}`}>{round.event.event_type.display_name}</Link>{' '}
              <span className="note">
                {round.event.scheduled_date} · {round.event.course} ({round.event.tee}) ·{' '}
                {round.event.state.replace(/_/g, ' ')}
              </span>
            </h4>
            <RoundLinks event={round.event} drawn={round.matches.some((m) => m.match_id !== null)} />
            <ul className="match-list">
              {round.matches.map((m) => (
                <li key={m.slot} className="match-block" data-testid={`bracket-slot-${round.round}-${m.slot}`}>
                  <div className="match-line">
                    <span>
                      {m.player_a_name ?? sourceText(m.source_a)} v{' '}
                      {m.player_b_name ?? sourceText(m.source_b)}
                    </span>
                    <span className="match-outcome">
                      {m.winner_player_id ? (
                        <>
                          <strong>
                            {m.winner_player_id === m.player_a_id
                              ? m.player_a_name
                              : m.player_b_name}
                          </strong>
                          {/* Golf's own notation, and it is in the record —
                              the final's "2 up" was sitting unused. "wins"
                              leads it, because a tie-break margin is a phrase
                              ("on hole 6") rather than a score. */}
                          <span className="match-margin">
                            {' wins'}
                            {m.margin ? ` ${m.margin}` : ''}
                          </span>
                        </>
                      ) : (
                        <em className="mark">{m.match_id ? 'to play' : 'not yet decided'}</em>
                      )}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>

      {bracket.undetermined.length > 0 && (
        <ul className="note note-warn undetermined" data-testid="bracket-undetermined">
          {bracket.undetermined.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}

      <p className="note">
        Playoff rounds are played and recorded like any other night, and award no points and no
        win-loss record — the regular season is a finished input to them.{' '}
        <Link to={`/seasons/${seasonId}/standings`}>The standings are here.</Link>
      </p>

      <p className="note">
        {/* Reachable rather than merely routed. It is also the honest framing:
            the projection reads TODAY's indexes, so its strokes are not the
            ones this bracket was played under — the frozen values above are. */}
        <Link to={`/seasons/${seasonId}/bracket/projection`}>
          See how this would be seeded from today&rsquo;s standings
        </Link>{' '}
        — recomputed from the current table and current handicap indexes, which is not what governed
        the bracket above.
      </p>
    </section>
  )
}

function ProjectedBracket({
  season,
  seasonId,
  projection,
}: {
  season: string
  seasonId: number
  projection: BracketProjection
}) {
  const { pathFor } = usePlayerPaths()
  return (
    <section className="bracket projection" data-testid="bracket-projection">
      <h2>
        Projected playoffs <span className="projection-tag">not official</span>
      </h2>

      <p className="projection-banner" role="status" data-testid="bracket-projection-banner">
        <strong>This is a projection, not the bracket.</strong> It is how the playoffs would be
        seeded if the season ended right now. Nothing here has been drawn, and the seeding becomes
        real only when the commissioner takes the cut.{' '}
        {projection.events_remaining === 0
          ? 'Every counting event has been played, so only the cut itself is outstanding.'
          : `${projection.events_remaining} counting ${
              projection.events_remaining === 1 ? 'event is' : 'events are'
            } still to play, and every number below can still move.`}
      </p>

      <SeasonSwitcher current={seasonId} view="bracket" />
      <p className="note">
        {season} · {projection.qualifiers} qualify · ordered on{' '}
        {projection.seeding_rule.order.map(tierName).join(', then ')} · as of{' '}
        {formatDate(projection.as_of)}.
      </p>

      {projection.seeds.length === 0 ? (
        <Empty>No event has been completed yet, so there is nothing to seed on.</Empty>
      ) : (
        <Seeds seeds={projection.seeds} qualifiers={projection.qualifiers} projected />
      )}

      {projection.matchups.length > 0 && (
        <>
          <h3>How it would be drawn</h3>
          <ul className="match-list" data-testid="projected-matchups">
            {projection.matchups.map((m) => (
              <li key={`${m.round}-${m.slot}`} className="match-block">
                <div className="match-line">
                  <span>
                    {nameOf(projection.seeds, m.player_a_id) ?? sourceText(m.source_a)} v{' '}
                    {nameOf(projection.seeds, m.player_b_id) ?? sourceText(m.source_b)}
                  </span>
                  <span className="match-outcome note">
                    {m.course ? `${m.course} (${m.tee})` : 'venue not decided'}
                  </span>
                </div>
                {m.strokes && (
                  /* Real figures, not an illustration: the playoff weeks are on
                     the calendar, so the course handicap follows from today's
                     index and the bonus from today's differential. The server
                     computes them with the code Start Round freezes with. */
                  <table className="table strokes-table">
                    <caption className="visually-hidden">
                      What each player would receive, at {m.strokes.allowance_pct}% allowance
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Player</th>
                        <th scope="col" className="numeric">
                          Index
                        </th>
                        <th scope="col" className="numeric">
                          CH
                        </th>
                        <th scope="col" className="numeric">
                          Bonus
                        </th>
                        <th scope="col" className="numeric">
                          Strokes
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.strokes.players.map((p) => (
                        <tr key={p.player_id}>
                          <th scope="row">
                            <Link to={pathFor(p.player_id)}>{p.display_name}</Link>
                          </th>
                          {/* Decimal strings throughout — the server does the
                              arithmetic (Constitution III). */}
                          <td className="numeric">{p.handicap_index}</td>
                          <td className="numeric">{p.course_handicap}</td>
                          <td className="numeric">{p.handicap_adjustment}</td>
                          <td className="numeric strong">{p.strokes_received}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {projection.undetermined.length > 0 && (
        <ul className="note note-warn undetermined" data-testid="bracket-undetermined">
          {projection.undetermined.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * The seeding, every player, with the cut drawn through it.
 *
 * The same table for a frozen seeding and a projected one, so the two cannot
 * differ in what they report — only in what surrounds them.
 */
function Seeds({
  seeds,
  qualifiers,
  projected,
}: {
  seeds: BracketSeed[]
  qualifiers: number
  projected?: boolean
}) {
  const { pathFor } = usePlayerPaths()
  return (
    <>
      <h3>{projected ? 'Seeding as it stands' : 'Seeding'}</h3>
      <div className="scroll-x">
        <table className="table seeds-table" data-testid="bracket-seeds">
          <caption className="visually-hidden">
            Every player, with the top {qualifiers} qualifying
          </caption>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Player</th>
              <th scope="col" className="numeric">
                Points
              </th>
              <th scope="col">Record</th>
              <th scope="col">In</th>
            </tr>
          </thead>
          <tbody>
            {seeds.map((s) => (
              <tr
                key={s.player_id}
                className={s.qualified ? 'seed-in' : 'seed-out'}
                data-testid={`seed-${s.seed}`}
              >
                <td className="numeric">{s.seed}</td>
                <th scope="row">
                  <Link to={pathFor(s.player_id)}>{s.display_name}</Link>
                  {s.tiebreak_applied && (
                    /* Named rather than inferred: two equal points columns do
                       not say which tier decided which side of the cut they
                       landed on. */
                    <em className="mark mark-tiebreak" data-testid={`tiebreak-${s.seed}`}>
                      {' '}
                      level on points — {tierName(s.tiebreak_applied)}
                    </em>
                  )}
                </th>
                <td className="numeric">{s.points}</td>
                <td className="numeric">
                  {s.matches_won}–{s.matches_lost}
                  {s.matches_halved > 0 && `–${s.matches_halved}`}
                </td>
                <td>{s.qualified ? 'yes' : <span className="mark mark-out">cut</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * Where a round's detail actually lives.
 *
 * # The bracket is a map, not a destination
 *
 * It answers "who plays whom" and nothing else — not what anyone is playing
 * off, and not what happened hole by hole. Both of those already have views,
 * and before this the only way to either was to type the URL: the round heading
 * linked to the scorecards and the match rows linked nowhere at all.
 *
 * **The stroke sheet is where the season-points bonus appears** (FR-034a,
 * FR-024f). It shows each pairing side by side with the whole derivation —
 * index, course handicap, allowance, the bonus, and the strokes that fall out —
 * which is the "checkable by hand" the requirement asks for and the one place
 * a member can see what a playoff match is being played off.
 */
function RoundLinks({ event, drawn }: { event: EventSummary; drawn: boolean }) {
  const playing = event.state === 'in_progress' || event.state === 'paused'
  return (
    <p className="note bracket-round-links">
      {/* Before it is played, what each player WOULD receive; afterwards, what
          they did — the same view either way, projecting or reading the frozen
          record (FR-024i, FR-027b).
          **Only once the round has a pairing.** A round still waiting on the
          one beneath it has nobody to compute strokes for, and the sheet says
          so with a 422 — which behind a link reads as a broken page rather
          than as a round that has not been drawn. */}
      {drawn ? (
        <Link to={`/events/${event.id}/stroke-sheet`}>Strokes and bonus</Link>
      ) : (
        <span>Strokes once this round is drawn</span>
      )}
      {playing && (
        <>
          {' · '}
          <Link to={`/events/${event.id}`} className="live-link">
            Live scores
          </Link>
          {' · '}
          <Link to={`/events/${event.id}/tracker`}>Round tracker</Link>
        </>
      )}
      {event.state === 'complete' && (
        <>
          {' · '}
          {/* The match-play record: hole by hole, who won each and the running
              status. A completed match-play round has no other rendering of
              itself. */}
          <Link to={`/events/${event.id}`}>Hole by hole</Link>
        </>
      )}
      {' · '}
      <Link to={`/events/${event.id}`}>Scorecards</Link>
    </p>
  )
}

/** What an undecided slot is waiting for, phrased as the bracket phrases it. */
function sourceText(src: BracketSource): string {
  if (src.seed !== undefined) return `seed ${src.seed}`
  if (src.winner_of) return `winner of round ${src.winner_of.round} match ${src.winner_of.slot}`
  return 'to be decided'
}

function tierName(tier: string): string {
  return tier.replace(/_/g, ' ')
}

function nameOf(seeds: BracketSeed[], playerId: string | null): string | undefined {
  if (!playerId) return undefined
  return seeds.find((s) => s.player_id === playerId)?.display_name
}

function formatDate(iso: string): string {
  return iso.slice(0, 10)
}

function isNotFound(error: Error): boolean {
  return error instanceof ApiError && error.status === 404
}

/** A load that never settles, for a fetch whose inputs are not ready. */
function never(): Promise<never> {
  return new Promise<never>(() => {})
}
